import { useEffect } from 'react';
import { addLanguageRequest } from '../services/language.service';
import {
  clearPendingLanguageRequest,
  peekPendingLanguageRequest,
} from '../utils/pendingLanguageRequest';

/**
 * The PendingLanguageRequest runner (LANGUAGE-REQUESTS.md §9, L4).
 *
 * Called at the TOP of ProtectedRoute, before the verification wall, so a
 * brand-new unverified account records the language it came for right after
 * signup; it counts once the email is verified, on any device.
 *
 * The stored request is cleared only once the server has it ('saved') or has
 * refused it for good ('refused': 409 slots_full / already_live / declined,
 * 422). Any other failure ('failed': network, 429, 5xx) keeps it, so the next
 * run sends it again with its ref; the languages page shows the failure with
 * a Try again (class B: a failed save tells the person).
 *
 * When the POST settles it fires LANGUAGES_CHANGED_EVENT with
 * {code, outcome}; the languages page reloads on it (React runs the page's
 * effects before this parent's, so the page's first load can precede the
 * request).
 */
export const LANGUAGES_CHANGED_EVENT = 'ee:languages-changed';

export type PendingLanguageOutcome = 'saved' | 'refused' | 'failed';

export interface LanguagesChangedDetail {
  code: string;
  outcome: PendingLanguageOutcome;
}

let inFlight: Promise<PendingLanguageOutcome> | null = null;

/** A refusal the server would repeat on every retry. */
function isDefinitiveRefusal(err: unknown): boolean {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status;
  return status === 409 || status === 422;
}

export function runPendingLanguageRequest(): Promise<PendingLanguageOutcome> | null {
  if (inFlight) return inFlight;
  const pending = peekPendingLanguageRequest();
  if (!pending) return null;
  const run = addLanguageRequest({ code: pending.code, ref: pending.ref, source: 'share_link' })
    .then(
      (): PendingLanguageOutcome => 'saved',
      (err: unknown): PendingLanguageOutcome => (isDefinitiveRefusal(err) ? 'refused' : 'failed'),
    )
    .then((outcome) => {
      if (outcome !== 'failed') clearPendingLanguageRequest();
      inFlight = null;
      window.dispatchEvent(
        new CustomEvent<LanguagesChangedDetail>(LANGUAGES_CHANGED_EVENT, {
          detail: { code: pending.code, outcome },
        }),
      );
      return outcome;
    });
  inFlight = run;
  return run;
}

export function usePendingLanguageRequest(ready: boolean): void {
  useEffect(() => {
    if (ready) void runPendingLanguageRequest();
  }, [ready]);
}
